/* eslint-disable camelcase */
import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import filteredProducts from 'shared/graphql/product/filtered-products.gql';
import { getQuantityRemaining } from 'shared/helpers/products';
import { parseProduct } from 'utils/helpers/product';
import { GqlOrders_Order } from 'types/graphql';

export type ReorderAvailableItem = {
  product: ReturnType<typeof parseProduct>;
  option: string;
  additionalOption?: string;
  quantity: number;
};

export type ReorderUnavailableItem = {
  name: string;
  image?: string;
  option?: string;
  reason: 'not_found' | 'out_of_stock';
  requestedQuantity: number;
  availableQuantity?: number;
};

export type ReorderItemsResult = {
  availableItems: ReorderAvailableItem[];
  unavailableItems: ReorderUnavailableItem[];
};

type UseReorderItemsReturn = {
  fetchReorderItems: (
    // eslint-disable-next-line camelcase
    orderItems: GqlOrders_Order[],
    dispensaryId: string,
    isKiosk: boolean
  ) => Promise<ReorderItemsResult>;
  loading: boolean;
  error: Error | null;
};

export function useReorderItems(): UseReorderItemsReturn {
  const apolloClient = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchReorderItems = useCallback(
    async (orderItems: GqlOrders_Order[], dispensaryId: string, isKiosk: boolean): Promise<ReorderItemsResult> => {
      setLoading(true);
      setError(null);

      try {
        const productIds = orderItems.map((item) => item.product?._id).filter((id): id is string => Boolean(id));

        const uniqueProductIds = [...new Set(productIds)];

        const { data } = await apolloClient.query({
          query: filteredProducts,
          variables: {
            productsFilter: {
              productIds: uniqueProductIds,
              dispensaryId,
              removeProductsBelowOptionThresholds: false,
              isKioskMenu: isKiosk,
              bypassKioskThresholds: isKiosk,
              bypassOnlineThresholds: true,
              Status: 'All',
            },
          },
          fetchPolicy: 'network-only',
        });

        const fetchedProducts = (data?.filteredProducts?.products ?? []).map(parseProduct);

        const categorized = categorizeOrderItems(orderItems, fetchedProducts, isKiosk);

        return categorized;
      } catch (err) {
        const fetchError = err instanceof Error ? err : new Error('Failed to fetch reorder items');
        setError(fetchError);
        return { availableItems: [], unavailableItems: [] };
      } finally {
        setLoading(false);
      }
    },
    [apolloClient]
  );

  return { fetchReorderItems, loading, error };
}

function categorizeOrderItems(
  orderItems: GqlOrders_Order[],
  fetchedProducts: ReturnType<typeof parseProduct>[],
  isKiosk: boolean
): ReorderItemsResult {
  const availableItems: ReorderAvailableItem[] = [];
  const unavailableItems: ReorderUnavailableItem[] = [];

  const productMap = new Map<string, ReturnType<typeof parseProduct>>();
  fetchedProducts.forEach((p) => {
    const key = String(p._id ?? p.id);
    if (key) productMap.set(key, p);
  });

  orderItems.forEach((orderItem) => {
    const productId = orderItem.product?._id;
    const productName = orderItem.product?.Name ?? 'Unknown Item';
    const option = orderItem.option ?? 'N/A';
    const requestedQuantity = orderItem.quantity ?? 1;
    const optionDisplay = option !== 'N/A' ? option : undefined;

    const matchedProduct = productId ? productMap.get(productId) : undefined;

    if (!matchedProduct || matchedProduct.Status !== 'Active') {
      unavailableItems.push({
        name: productName,
        image: orderItem.product?.Image ?? undefined,
        option: optionDisplay,
        reason: matchedProduct ? 'out_of_stock' : 'not_found',
        requestedQuantity,
      });
      return;
    }

    const quantityRemaining = getQuantityRemaining(matchedProduct, option, { isKiosk });
    if (quantityRemaining <= 0) {
      unavailableItems.push({
        name: productName,
        image: orderItem.product?.Image ?? undefined,
        option: optionDisplay,
        reason: 'out_of_stock',
        requestedQuantity,
        availableQuantity: 0,
      });
      return;
    }

    const quantityToAdd = Math.min(requestedQuantity, quantityRemaining);

    availableItems.push({
      product: matchedProduct,
      option,
      additionalOption: orderItem.additionalOption ?? undefined,
      quantity: quantityToAdd,
    });

    if (quantityToAdd < requestedQuantity) {
      unavailableItems.push({
        name: productName,
        image: orderItem.product?.Image ?? undefined,
        option: optionDisplay,
        reason: 'out_of_stock',
        requestedQuantity,
        availableQuantity: quantityToAdd,
      });
    }
  });

  return { availableItems, unavailableItems };
}
